/* @flow */

import {
  PREBID_UID_PUBLINKID,
  PREBID_UID_PUBCOMMON,
  PREBID_UID_LIVERAMP,
  PREBID_UID_VERIZON,
  PREBID_UID_CRITEO,
  PREBID_UID_FABRICK,
  PREBID_UID_UNIFIEDID,
  PREBID_UID_LOTAMEPANORAMAID,
  PREBID_UID_PARRABLEID,
  PREBID_UID_ID5ID,
  PREBID_UID_33ACROSS,
  PREBID_UID_PUBPROVIDEDID,
  PREBID_UID_GOOGLEPAIRID,
  IDP_PUBMATIC,
  IDP_VZM,
  IDP_PUBLINK,
  IDP_33ACROSS,
  IDP_LIVERAMP,
  IDP_FABRICK,
  IDP_COMMONID,
  IDP_ID5,
  USER_ID_33ACROSS,
  USER_ID_PUBLINK,
  USER_ID_VERIZON,
  USER_ID_CRITEO,
  USER_ID_FABRICK,
  USER_ID_UNIFIEDID,
  USER_ID_LOTAMEPANORAMA,
  USER_ID_PARRABLE,
  USER_ID_PUBCOMMON,
  USER_ID_ID5,
  USER_ID_GOOGLEPAIRID,
  USER_ID_LIVERAMP
} from './idpconstants';
import idp from './idpmanager';
import { logDebug } from '../utils/logger';
import { isJSONSupported } from '../utils/browserutil';
import {
  isNonEmptyObject,
  isFunction,
  noop,
  isString,
  isArray
} from '../utils/coreutils';
import { getTopLevelDomain } from '../utils';
import { isOptedOut } from '../utils/privacyutils';
import getCVars from '../utils/gputils';

// make sure to update this whenever we add a new ID
const allUIDs = [
  PREBID_UID_PUBLINKID,
  PREBID_UID_PUBCOMMON,
  PREBID_UID_LIVERAMP,
  PREBID_UID_VERIZON,
  PREBID_UID_CRITEO,
  PREBID_UID_FABRICK,
  PREBID_UID_PUBPROVIDEDID,
  PREBID_UID_LOTAMEPANORAMAID,
  PREBID_UID_PARRABLEID,
  PREBID_UID_ID5ID,
  PREBID_UID_33ACROSS,
  USER_ID_GOOGLEPAIRID,
  PREBID_UID_UNIFIEDID
];

/**
 * @param {string} idprovider - IDProvider name
 * @param {string} idpKey - Additional key required to fetch individual ID values multi ID providers
 * @returns {string} ID value
 */
function _getIDPId(
  idprovider: string = '',
  idpKey: string = ''
): string | Array<string> {
  const id = idp.getUserID(idprovider, idpKey);
  logDebug(
    `Provider: ${idprovider}, key: ${idpKey}, value : ${[id].flat().join(',')}`
  );
  return id;
}

/**
 * @param {string} idprovider - IDProvider name
 * @param {string} idpKey - Additional key required to fetch individual ID values multi ID providers
 * @returns {object} Ext data object
 */
function _getIDPExt(idprovider: string = '', idpKey: string = ''): anyObject {
  const ext = idp.getUserIDExt(idprovider, idpKey);
  if (isJSONSupported()) {
    logDebug(
      `Provider: ${idprovider}, key: ${idpKey}, Ext value : ${JSON.stringify(
        ext
      )}`
    );
  }

  return ext;
}

const getterMap = {
  [PREBID_UID_PUBCOMMON]: [
    {
      type: 'id',
      idprovider: IDP_COMMONID,
      idpKey: USER_ID_PUBCOMMON
    }
  ],
  [PREBID_UID_LIVERAMP]: [
    {
      type: 'id',
      idprovider: IDP_LIVERAMP,
      idpKey: USER_ID_LIVERAMP
    }
  ],
  [PREBID_UID_GOOGLEPAIRID]: [
    {
      type: 'id',
      idprovider: IDP_LIVERAMP,
      idpKey: USER_ID_GOOGLEPAIRID
    }
  ],
  [PREBID_UID_33ACROSS]: [
    {
      type: 'id',
      idprovider: IDP_33ACROSS,
      idpKey: USER_ID_33ACROSS
    }
  ],
  [PREBID_UID_CRITEO]: [
    {
      type: 'id',
      idprovider: IDP_PUBMATIC,
      idpKey: USER_ID_CRITEO
    }
  ],
  [PREBID_UID_FABRICK]: [
    {
      type: 'id',
      idprovider: IDP_FABRICK,
      idpKey: USER_ID_FABRICK
    }
  ],
  [PREBID_UID_UNIFIEDID]: [
    {
      type: 'id',
      idprovider: IDP_PUBMATIC,
      idpKey: USER_ID_UNIFIEDID
    }
  ],
  [PREBID_UID_LOTAMEPANORAMAID]: [
    {
      type: 'id',
      idprovider: IDP_PUBMATIC,
      idpKey: USER_ID_LOTAMEPANORAMA
    }
  ],
  [PREBID_UID_PARRABLEID]: [
    {
      type: 'id',
      idprovider: IDP_PUBMATIC,
      idpKey: USER_ID_PARRABLE
    }
  ],
  [PREBID_UID_ID5ID]: [
    {
      type: 'id',
      idprovider: IDP_ID5,
      idpKey: USER_ID_ID5
    },
    {
      type: 'idExt',
      idprovider: IDP_ID5,
      idpKey: USER_ID_ID5
    }
  ],
  [PREBID_UID_VERIZON]: [
    {
      type: 'id',
      idprovider: IDP_VZM,
      idpKey: USER_ID_VERIZON
    }
  ],
  [PREBID_UID_PUBLINKID]: [
    {
      type: 'id',
      idprovider: IDP_PUBLINK,
      idpKey: USER_ID_PUBLINK
    }
  ]
};

function _getIDCommon(
  prebidIdp = '',
  idType = 'id'
): string | Array<string> | anyObject {
  if (prebidIdp.length === 0) {
    return '';
  }
  // check if we have mapping
  // $FlowFixMe prop-missing
  const arr = getterMap[prebidIdp];
  if (!isArray(arr)) {
    return '';
  }
  const filtered = arr.filter(({ type }) => type === idType);
  const fn = idType === 'id' ? _getIDPId : _getIDPExt;
  let ret = '';
  // we may have more fn arrays here. Continue until we get valid response.
  // Will be easier to add fallback.
  filtered.some(({ idprovider, idpKey }) => {
    ret = fn(idprovider, idpKey);
    return ret;
  });
  return ret;
}

/**
 * @param {string} id - Prebid mapped ID
 * @returns  {string} - User ID
 */
export function getUserID(id: string = ''): string | Array<string> {
  const ret = '';
  if (id.length === 0) {
    return ret;
  }
  // $FlowFixMe
  return _getIDCommon(id);
}

/**
 * @param {string} id - Prebid mapped ID
 * @returns  {anyObject} - Ext data object
 */
export function getUIDExt(id: string = ''): anyObject {
  const ret = {};
  if (id.length === 0) {
    return ret;
  }
  // $FlowFixMe
  return _getIDCommon(id, 'idExt');
}

function _getRTBProvidedIDs(
  eidKeys: $ReadOnlyArray<string> = []
): Array<RTBUserID> {
  // get SHA256 and send it
  const { _uh6: id = '' } = getCVars();
  if (
    isOptedOut() ||
    eidKeys.length === 0 ||
    !eidKeys.includes(PREBID_UID_PUBPROVIDEDID) ||
    id.length === 0
  ) {
    return [];
  }

  return [
    {
      source: getTopLevelDomain(),
      uids: [
        {
          atype: 1,
          id,
          ext: {
            stype: 'sha256email' // allowable options are sha256email, DMP, ppuid for now
          }
        }
      ]
    }
  ];
}

export const EID_KEYS_SOURCE_MAP: { [string]: string } = {
  [PREBID_UID_PUBLINKID]: 'epsilon.com',
  [PREBID_UID_LIVERAMP]: 'liveramp.com',
  [PREBID_UID_GOOGLEPAIRID]: 'google.com',
  [PREBID_UID_VERIZON]: 'yahoo.com',
  [PREBID_UID_PUBCOMMON]: 'pubcid.org',
  [PREBID_UID_CRITEO]: 'criteo.com',
  [PREBID_UID_UNIFIEDID]: 'adserver.org',
  [PREBID_UID_LOTAMEPANORAMAID]: 'crwdcntrl.net',
  [PREBID_UID_PARRABLEID]: 'parrable.com',
  [PREBID_UID_ID5ID]: 'id5-sync.com',
  [PREBID_UID_33ACROSS]: '33across.com',
  [PREBID_UID_FABRICK]: 'neustar.biz'
};
/**
 * Get RTB User Object with Eid array based on the keys passed
 *
 * @param {object} eidKeys - Array of User ID module keys to get values for
 * @returns {boolean} - Object with Array of Eid objects for keys requested
 * @access private
 */
function _getRTBUserEidsPartners(
  eidKeys: $ReadOnlyArray<string> = []
): Array<RTBUserID> {
  /* User ID modules information */
  const EidsMap = {
    [PREBID_UID_PUBLINKID]: {
      fn: getUserID,
      args: [PREBID_UID_PUBLINKID],
      source: EID_KEYS_SOURCE_MAP[PREBID_UID_PUBLINKID],
      atype: 3
    },
    [PREBID_UID_LIVERAMP]: {
      fn: getUserID,
      args: [PREBID_UID_LIVERAMP],
      source: EID_KEYS_SOURCE_MAP[PREBID_UID_LIVERAMP],
      atype: 3
    },
    [PREBID_UID_GOOGLEPAIRID]: {
      fn: getUserID,
      args: [PREBID_UID_LIVERAMP],
      source: EID_KEYS_SOURCE_MAP[PREBID_UID_GOOGLEPAIRID],
      atype: 571187
    },
    // Verizon Media ConnectID
    [PREBID_UID_VERIZON]: {
      fn: getUserID,
      args: [PREBID_UID_VERIZON],
      source: EID_KEYS_SOURCE_MAP[PREBID_UID_VERIZON],
      atype: 3
    },
    [PREBID_UID_PUBCOMMON]: {
      fn: getUserID,
      args: [PREBID_UID_PUBCOMMON],
      source: EID_KEYS_SOURCE_MAP[PREBID_UID_PUBCOMMON],
      atype: 1
    },
    [PREBID_UID_CRITEO]: {
      fn: getUserID,
      args: [PREBID_UID_CRITEO],
      source: EID_KEYS_SOURCE_MAP[PREBID_UID_CRITEO],
      atype: 1
    },
    [PREBID_UID_UNIFIEDID]: {
      fn: getUserID,
      args: [PREBID_UID_UNIFIEDID],
      source: EID_KEYS_SOURCE_MAP[PREBID_UID_UNIFIEDID],
      atype: 1
    },
    [PREBID_UID_LOTAMEPANORAMAID]: {
      fn: getUserID,
      args: [PREBID_UID_LOTAMEPANORAMAID],
      source: EID_KEYS_SOURCE_MAP[PREBID_UID_LOTAMEPANORAMAID],
      atype: 1
    },
    [PREBID_UID_PARRABLEID]: {
      fn: getUserID,
      args: [PREBID_UID_PARRABLEID],
      source: EID_KEYS_SOURCE_MAP[PREBID_UID_PARRABLEID],
      atype: 1
    },
    [PREBID_UID_ID5ID]: {
      fn: getUserID,
      args: [PREBID_UID_ID5ID],
      source: EID_KEYS_SOURCE_MAP[PREBID_UID_ID5ID],
      atype: 1,
      extFn: getUIDExt
    },
    [PREBID_UID_33ACROSS]: {
      fn: getUserID,
      args: [PREBID_UID_33ACROSS],
      source: EID_KEYS_SOURCE_MAP[PREBID_UID_33ACROSS],
      atype: 1
    },
    [PREBID_UID_FABRICK]: {
      fn: getUserID,
      args: [PREBID_UID_FABRICK],
      source: EID_KEYS_SOURCE_MAP[PREBID_UID_FABRICK],
      atype: 1
    }
  };

  // if no argument are passed, assume that partner needs all
  const reqEids = eidKeys.length > 0 ? eidKeys : Object.keys(EidsMap);

  /* Add eid data for the User IDs we have in the Eids map */
  const eids = reqEids
    .filter(eidKey => EidsMap[eidKey] && isFunction(EidsMap[eidKey].fn))
    .reduce((_eids, idKey) => {
      const { fn, args, source, atype, extFn = noop } = EidsMap[idKey];
      const id = fn(...args);
      // Get ext data from IDP partner
      const extVal = extFn(...args);
      const xtraVals = {};
      if (isNonEmptyObject(extVal)) {
        xtraVals.ext = extVal;
      }

      if (id.length > 0) {
        _eids.push({
          source,
          uids: [
            {
              id,
              atype,
              ...xtraVals
            }
          ]
        });
      }
      return _eids;
    }, []);
  return eids;
}

export function getRTBUserEids(
  eidKeys: $ReadOnlyArray<string> = allUIDs
): RTBUserIDs {
  const eids = [
    ..._getRTBUserEidsPartners(eidKeys),
    ..._getRTBProvidedIDs(eidKeys)
  ];
  if (eids.length === 0) {
    return {};
  }
  return {
    user: {
      eids
    }
  };
}

export function getUserIds(
  eidKeys: $ReadOnlyArray<string> = allUIDs
): anyObject {
  const userIdsMap = {
    [EID_KEYS_SOURCE_MAP[PREBID_UID_PUBLINKID]]: eid => ({
      [PREBID_UID_PUBLINKID]: eid.uids[0].id
    }),
    [EID_KEYS_SOURCE_MAP[PREBID_UID_LIVERAMP]]: eid => ({
      [PREBID_UID_LIVERAMP]: eid.uids[0].id
    }),
    [EID_KEYS_SOURCE_MAP[PREBID_UID_GOOGLEPAIRID]]: eid => ({
      [PREBID_UID_GOOGLEPAIRID]: eid.uids[0].id
    }),
    [EID_KEYS_SOURCE_MAP[PREBID_UID_VERIZON]]: eid => ({
      [PREBID_UID_VERIZON]: eid.uids[0].id
    }),
    [EID_KEYS_SOURCE_MAP[PREBID_UID_PUBCOMMON]]: eid => ({
      [PREBID_UID_PUBCOMMON]: eid.uids[0].id
    }),
    [EID_KEYS_SOURCE_MAP[PREBID_UID_CRITEO]]: eid => ({
      [PREBID_UID_CRITEO]: eid.uids[0].id
    }),
    [EID_KEYS_SOURCE_MAP[PREBID_UID_UNIFIEDID]]: eid => ({
      [PREBID_UID_UNIFIEDID]: eid.uids[0].id
    }),
    [EID_KEYS_SOURCE_MAP[PREBID_UID_LOTAMEPANORAMAID]]: eid => ({
      [PREBID_UID_LOTAMEPANORAMAID]: eid.uids[0].id
    }),
    [EID_KEYS_SOURCE_MAP[PREBID_UID_PARRABLEID]]: eid => ({
      [PREBID_UID_PARRABLEID]: eid.uids[0].id
    }),
    [EID_KEYS_SOURCE_MAP[PREBID_UID_ID5ID]]: eid => ({
      [PREBID_UID_ID5ID]: {
        uid: eid.uids[0].id,
        ext: eid.uids[0].ext
      }
    }),
    [EID_KEYS_SOURCE_MAP[PREBID_UID_33ACROSS]]: eid => ({
      [PREBID_UID_33ACROSS]: {
        envelope: eid.uids[0].id
      }
    }),
    [EID_KEYS_SOURCE_MAP[PREBID_UID_FABRICK]]: eid => ({
      [PREBID_UID_FABRICK]: eid.uids[0].id
    }),

    [getTopLevelDomain()]: eid => ({
      [PREBID_UID_PUBPROVIDEDID]: [
        {
          source: eid.source,
          uids: [
            {
              id: eid.uids[0].id,
              atype: eid.uids[0].atype,
              ext: eid.uids[0].ext
            }
          ]
        }
      ]
    })
  };
  const eids = getRTBUserEids(eidKeys);
  return eids.user.eids.reduce((prev, eid) => {
    const value = userIdsMap[eid.source](eid);
    return { ...prev, ...value };
  }, {});
}

export function getRTBUserEidBySource(source: string = ''): RTBUserID {
  if (source.length === 0) {
    // we need to get all the values
    return {};
  }

  // get UserID based on source
  const reqEids = Object.entries(EID_KEYS_SOURCE_MAP)
    .filter(([, src]) => source === src)
    .map(([uid]) => uid);
  // as we are always querying only one Eid,
  // just return only that to the caller
  const { user: { eids: [eid = {}] = [] } = {} } = getRTBUserEids(reqEids);
  return eid;
}

function _encryptIDPValue(
  signal: RTBUserID | string | Array<string>,
  version: number = 1
): string {
  // $FlowFixMe[incompatible-type]
  let s: string = isString(signal) ? signal : JSON.stringify(signal);
  switch (version) {
    case 1: {
      s = btoa(s);
      break;
    }
    default: {
      break;
    }
  }
  s = `${version}||${s}`;
  return s;
}

function _getEncryptedIDP(
  source: string,
  encrypt: boolean,
  encryptVersion: number,
  uolIDP: string
): string | Array<string> {
  const sRet = '';
  if (source.length === 0) {
    return sRet;
  }
  const querySource = uolIDP || source;
  const res = getRTBUserEidBySource(querySource);
  const { uids: [{ id = '' } = {}] = [] } = res;
  // if this is empty object, nothing to return here
  if (!isNonEmptyObject(res) || id.length === 0) {
    return sRet;
  }
  // if we are not encrypting, return the id as it is;
  if (!encrypt) {
    return id;
  }
  // send encrypted ID here
  return _encryptIDPValue(id, encryptVersion);
}

export function getEncryptedIDP(
  source: string = '',
  encrypt: boolean = false,
  encryptVersion: number = 1,
  uolIDP: string = ''
): Promise<string | Array<string>> {
  const ret = _getEncryptedIDP(source, encrypt, encryptVersion, uolIDP);
  logDebug(
    `Encrypted signal ${source} - Encryption:${
      encrypt ? 1 : 0
    }-${encryptVersion} ${JSON.stringify(ret)}`
  );
  return Promise.resolve(ret);
}

/**
 * All partners sources listed in ESS may not be enabled/active.
 * Filter and register only for the active one.
 *
 * @param {Array<string>} sources - List of sources for ESS
 * @returns {Array<string>}
 */
export function filterEnabledSources(
  sources: Array<string> = []
): Array<string> {
  let ret = [];
  if (sources.length === 0) {
    return ret;
  }

  const sourcesSet = new Set(sources);
  const prebidIds = Object.entries(EID_KEYS_SOURCE_MAP)
    .filter(([, source]) => sourcesSet.has(source))
    .map(([prebidId]) => prebidId);

  // check if the partner is enabled.
  ret = prebidIds
    .filter(prebidId => {
      // get object from getterMap
      const m = (getterMap[prebidId] || []).filter(({ type }) => type === 'id');
      if (m.length === 0) {
        return false;
      }
      return m.some(({ idprovider, idpKey }) =>
        idp.isUserIdEnabled(idprovider, idpKey)
      );
    })
    .map(prebidId => EID_KEYS_SOURCE_MAP[prebidId]);
  logDebug(
    `ESS source filter source:${sources.join(',')} ret:${ret.join(',')}`
  );
  return ret;
}
